/**
 * Utility functions for safe localStorage access
 * Handles cases where localStorage might not be available (SSR, private browsing, etc.)
 */

/**
 * Check if localStorage is available in the current environment
 */
export const isLocalStorageAvailable = (): boolean => {
  try {
    if (typeof window === 'undefined') {
      return false;
    }

    const testKey = '__localStorage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

/**
 * Safely get an item from localStorage
 * @param key - The key to retrieve
 * @returns The value or null if localStorage is not available or key doesn't exist
 */
export const safeGetItem = (key: string): string | null => {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

/**
 * Safely set an item in localStorage
 * @param key - The key to set
 * @param value - The value to store
 * @returns true if successful, false otherwise
 */
export const safeSetItem = (key: string, value: string): boolean => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

/**
 * Safely remove an item from localStorage
 * @param key - The key to remove
 * @returns true if successful, false otherwise
 */
export const safeRemoveItem = (key: string): boolean => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

/**
 * Safely clear all items from localStorage
 * @returns true if successful, false otherwise
 */
export const safeClear = (): boolean => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    localStorage.clear();
    return true;
  } catch {
    return false;
  }
};

/**
 * Safely reload the page
 * @returns true if successful, false if window is not available
 */
export const safeReload = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    window.location.reload();
    return true;
  } catch {
    return false;
  }
};

/**
 * Safely navigate to a URL
 * @param url - The URL to navigate to
 * @returns true if successful, false if window is not available
 */
export const safeNavigate = (url: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    window.location.href = url;
    return true;
  } catch {
    return false;
  }
};

/**
 * Safely go back in browser history
 * @returns true if successful, false if window is not available
 */
export const safeGoBack = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    window.history.back();
    return true;
  } catch {
    return false;
  }
};
